'use client';
import {
  isServer,
  QueryCache,
  QueryClient,
  QueryClientProvider as RQ_QueryClientProvider,
} from '@tanstack/react-query';

import { isStaleDeploymentError } from '@/lib/utils/stale_deployment';
import { isClientWebApiError, requiresPageReload } from '@/lib/web_api/web_api_error';

const MAX_QUERY_RETRIES = 3;
const SESSION_RELOAD_GUARD_KEY = 'much:web-api-session-reload';
const SESSION_RELOAD_GUARD_MS = 30_000;

/**
 * A read rejected for its session (signed out, MFA, no company) reloads the page, so the page
 * itself redirects to login, MFA or setup and returns here afterwards. A read rejected because
 * another tab switched the company reloads the page into that company. The guard stops a reload
 * loop if the page loads but its reads keep failing.
 */
function reloadPage(error: unknown) {
  if (isServer || !requiresPageReload(error)) return;

  const lastReload = Number(window.sessionStorage.getItem(SESSION_RELOAD_GUARD_KEY) ?? 0);
  if (Date.now() - lastReload < SESSION_RELOAD_GUARD_MS) return;

  window.sessionStorage.setItem(SESSION_RELOAD_GUARD_KEY, String(Date.now()));
  window.location.reload();
}

function makeQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({ onError: reloadPage }),
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        // Client errors repeat the same answer, and a Server Action this deployment does not know
        // can never succeed (only a reload helps), so surface both at once instead of after the backoff.
        retry: (failureCount, error) =>
          !isClientWebApiError(error) && !isStaleDeploymentError(error) && failureCount < MAX_QUERY_RETRIES,
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

export function QueryClientProvider({ children }: { children: React.ReactNode }) {
  const queryClient = getQueryClient();

  return <RQ_QueryClientProvider client={queryClient}>{children}</RQ_QueryClientProvider>;
}
